import React, { forwardRef, useEffect, useMemo } from "react";
import { defaultMemoize } from "reselect";

import { ControlReduxRawValue, ExplicitValidation, InstantValidation } from "@multiweb/reactControls";
import { EMPTY_ARRAY } from "@multiweb/reactUtils/constants";
import { IconCode } from "@multiweb/types/contracts";
import { ForwardRefType } from "@multiweb/types/react";
import { EnumUtils } from "Global/Base/Utils/EnumUtils";
import { ControlValueWrapper } from "../ControlValueWrapper";
import { FormComponentPropsWrapper } from "../FormComponentPropsWrapper";
import { DataAttributesProps, EditableControlComponentProps } from "../types";
import { ValidationErrorWrapper } from "../ValidationErrorWrapper";
import { IconEditConnected } from "./IconEditConnected";

export type IconEditProps = {
    /** Velikost ikony */
    iconSize?: number;
    /** uuid konektoru */
    uuid: string;
} & DataAttributesProps & EditableControlComponentProps;

export const IconEdit: ForwardRefType<HTMLButtonElement, IconEditProps> = forwardRef<HTMLButtonElement, IconEditProps>(({
    id,
    dataKey,
    maxLength,
    validationRegex,
    externalErrors,
    isInRequiredInvalidState,
    onValidationCallback,
    onValidityChanged,
    onBlur,
    onMount,
    onFocus,
    onMouseEnter,
    onMouseLeave,
    onMouseOver,
    onClick,
    enabledVisual,
    recommended,
    required,
    postponedRequired,
    iconSize,
    uuid,
    isEditing,
    encrypted,
// Reference je zde kvůli EditableControlConnectoru.
// @ts-ignore
// eslint-disable-next-line unused-imports/no-unused-vars
}, ref) => {
    // useMemo vrací memoizovanou funkci, která je přepočítána jenom v případě, kdy se mění parametr maxLength nebo rawValue.
    // Pokud jsou obě hodnoty stejné jako při posledním volání, nic nepřepočítává a vrací předchozí funkci.
    const createInstantDesc = useMemo(() => defaultMemoize((maxLength: number | undefined, rawValue: ControlReduxRawValue | undefined) =>
        maxLength ? [InstantValidation.createLengthDescription(rawValue, maxLength)] : EMPTY_ARRAY,
    ), []);
    const explicitValidationFnDescriptions = useMemo(() => validationRegex ? [ExplicitValidation.createRegexDescription(validationRegex)] : EMPTY_ARRAY, [validationRegex]);

    useEffect(() => {
        onMount && onMount({
            getDisplayedValue: (rawValue) => rawValue,
            getControlValue: (rawValue) => rawValue,
            convertControlValueToRawValue: (controlValue) => controlValue,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <ControlValueWrapper dataKey={dataKey}>
            {({ rawValue }) => {
                const instantValidationFnDescriptions = createInstantDesc(maxLength, rawValue);

                return (
                    <ValidationErrorWrapper {...{ dataKey, instantValidationFnDescriptions, explicitValidationFnDescriptions, externalErrors, isInRequiredInvalidState, onValidityChanged }}>
                        {({ invalid, validate }) => (
                            <FormComponentPropsWrapper {...{ postponedRequired, required, recommended, enabledVisual }}>
                                {({ disabled, backgroundColor, readOnly }) => {
                                    return (
                                        <IconEditConnected
                                            {...{ id, iconSize, onFocus, onMouseEnter, onMouseLeave, onMouseOver, uuid, isEditing, backgroundColor, readOnly, invalid, encrypted, disabled }}
                                            iconCode={ EnumUtils.hasEnumValue(IconCode, rawValue?.toString()) ? rawValue?.toString() : undefined }
                                            onBlur={(e) => {
                                                validate(onValidationCallback);
                                                onBlur && onBlur(e);
                                            }}
                                            onClick={(e) => {
                                                onClick && onClick(e);
                                            }}
                                        />
                                    );
                                }}
                            </FormComponentPropsWrapper>
                        )}
                    </ValidationErrorWrapper>
                );
            }
            }
        </ControlValueWrapper>
    );
});
